import { AppType } from "client/Api/Apps/common";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration
} from "client/Validation";
import {
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useImperativeHandle,
	useState
} from "react";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import {
	Checkbox,
	CheckboxGroup,
	CheckboxGroupItem,
	FormItem,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	EditAddressBookAppViewModel,
	SaveAddressBookAppModel,
	saveAddressBookApp,
	ConfigurableAddressBookAppContactSource
} from "client/Api/Apps/EditApp/saveApp";

export type EditAddressBookAppProps = {
	initialSettings: EditAppSettings<EditAddressBookAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditAddressBookApp = forwardRef(
	(
		props: EditAddressBookAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [formData, setFormData] = useState<SaveAddressBookAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveAddressBookAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveAddressBookAppModel>(
			formHandler,
			saveAddressBookApp,
			AppType.AddressBook
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("contactSources")
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>

				<FormItem
					label={localize("_Sources")}
					isForGroup
				>
					<Checkbox
						value
						text={localize("_WorkspaceContacts")}
						isDisabled
						title={localize("_WorkspaceContactsCantBeTurnedOffInTheAddressBook")}
						onChange={() => {/** Noop */}}
					/>
					<CheckboxGroup
						{...formHandler.for("contactSources").attachToCheckboxGroup()}
						options={options}
					/>
					<ValidationError
						{...formHandler.for("contactSources").attachToValidationError()}
					/>
				</FormItem>
			</EditAppBase>
		);
	}
);
EditAddressBookApp.displayName = "EditAddressBookApp";

const options: readonly CheckboxGroupItem<ConfigurableAddressBookAppContactSource>[] = [
	{
		text: localize("_PersonalContacts"),
		value: ConfigurableAddressBookAppContactSource.Personal
	},
	{
		text: localize("_OrganisationContacts"),
		value: ConfigurableAddressBookAppContactSource.Gal
	}
];

const validation = createValidationConfiguration<SaveAddressBookAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

function convertToSaveModel(
	initialSettings: EditAppSettings<EditAddressBookAppViewModel>
): SaveAddressBookAppModel {
	const {
		contactSources
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		contactSources
	};
}